import React, { useState } from 'react';
import type { IGrants } from '../utils/configEditorTypes';
import { TrashIcon } from '@heroicons/react/24/outline';
import DropdownSingleselect from 'components/DropdownSingleselect';

interface GrantEditorProps {
  grants: IGrants;
  setGrants: (grants: IGrants) => void;
}

export default function GrantEditor(props: GrantEditorProps): JSX.Element {
  const [showAddGrant, setShowAddGrant] = useState<boolean>(false);
  const [newPrivilege, setNewPrivilege] = useState<string>('SELECT');
  const [newGrantee, setNewGrantee] = useState<string>('');

  // Available privileges for the dropdown
  const availablePrivileges = [
    'SELECT',
    'INSERT',
    'UPDATE',
    'DELETE',
    'REFERENCES',
    'USAGE',
    'OWNERSHIP',
    'MONITOR',
    'OPERATE',
    'ACCOUNTADMIN',
  ];

  const handleToggleGrantOption = (privilege: string, role: string): void => {
    const updatedGrants = props.grants !== undefined ? { ...props.grants } : {};

    if (updatedGrants[privilege] === undefined) {
      updatedGrants[privilege] = [{ [role]: { with_grant_option: true } }];
    } else {
      const roleEntry = updatedGrants[privilege].find((entry) => Object.keys(entry)[0] === role);

      if (roleEntry !== undefined) {
        const currentOption = roleEntry[role]?.with_grant_option;
        roleEntry[role] = { with_grant_option: currentOption !== undefined ? !currentOption : false };
      } else {
        updatedGrants[privilege].push({ [role]: { with_grant_option: true } });
      }
    }

    props.setGrants(updatedGrants);
  };

  const handleAddGrant = (): void => {
    if (newPrivilege === '' || newGrantee === '') {
      return;
    }

    const updatedGrants = props.grants !== undefined ? { ...props.grants } : {};

    if (updatedGrants[newPrivilege] === undefined) {
      updatedGrants[newPrivilege] = [{ [newGrantee]: { with_grant_option: false } }];
    } else {
      // Check if this role already has this privilege
      const existingRoleEntry = updatedGrants[newPrivilege].find((entry) => Object.keys(entry)[0] === newGrantee);

      if (existingRoleEntry === undefined) {
        updatedGrants[newPrivilege].push({ [newGrantee]: { with_grant_option: false } });
      }
    }

    props.setGrants(updatedGrants);
    setNewGrantee('');
    setShowAddGrant(false);
  };

  const handleDeleteGrant = (privilege: string, role: string): void => {
    // Return early if grants is null or undefined
    if (props.grants === undefined) {
      return;
    }

    const updatedGrants = { ...props.grants };

    if (updatedGrants[privilege] !== undefined) {
      // Filter out the specific role entry
      updatedGrants[privilege] = updatedGrants[privilege].filter((entry) => Object.keys(entry)[0] !== role);

      // If no roles left for this privilege, remove the privilege entry
      if (updatedGrants[privilege].length === 0) {
        // Remove the property in a type-safe way
        const { [privilege]: _, ...rest } = updatedGrants;
        props.setGrants(rest);
        return;
      }

      props.setGrants(updatedGrants);
    }
  };

  const handleCancel = (): void => {
    setNewGrantee('');
    setNewPrivilege('SELECT');
    setShowAddGrant(false);
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Grants</h2>
        <button
          type="button"
          onClick={() => setShowAddGrant(true)}
          className="inline-flex items-center px-4 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <span className="mr-1">+</span> Add Grant
        </button>
      </div>

      {showAddGrant && (
        <div className="p-4 border rounded-lg mb-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <DropdownSingleselect
                label="Privilege"
                options={availablePrivileges}
                selected={newPrivilege}
                setSelected={setNewPrivilege}
              />
            </div>
            <div className="w-full">
              <label htmlFor="grantee" className="text-sm font-medium leading-6 text-gray-900 flex items-center">
                Grantee
              </label>
              <div className="relative mt-2">
                <input
                  id="grantee"
                  type="text"
                  value={newGrantee}
                  onChange={(e) => setNewGrantee(e.target.value)}
                  placeholder="Enter name"
                  className="w-full min-h-[36px] rounded-md py-1.5 pl-1.5 pr-3 text-left shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-start space-x-3">
            <button
              type="button"
              onClick={handleAddGrant}
              className="inline-flex items-center px-4 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dataops-primary hover:bg-dataops-primary-hover"
            >
              Add Grant
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        {props.grants === undefined || Object.keys(props.grants).length === 0 ? (
          <div className="py-2 px-2 text-center text-sm text-gray-500">No grants have been added yet</div>
        ) : (
          <>
            <div className="grid grid-cols-8 bg-gray-50">
              <div className="col-span-2 py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                PRIVILEGE
              </div>
              <div className="col-span-3 py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                GRANTEE
              </div>
              <div className="col-span-2 py-2 px-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                GRANT OPTION
              </div>
              <div className="col-span-1 py-2 px-2"></div>
            </div>

            {Object.entries(props.grants).map(([privilege, roleEntries]) =>
              roleEntries.map((roleEntry) => {
                const role = Object.keys(roleEntry)[0];
                const withGrantOption = roleEntry[role]?.with_grant_option ?? false;

                return (
                  <div key={`${privilege}-${role}`} className="grid grid-cols-8 border-t border-gray-200">
                    <div className="col-span-2 py-2 px-2 text-sm font-medium text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {privilege}
                    </div>
                    <div className="col-span-3 py-2 px-2 text-sm text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                      {role}
                    </div>
                    <div className="col-span-2 py-2 px-2 flex justify-center">
                      <input
                        type="checkbox"
                        checked={withGrantOption}
                        onChange={() => handleToggleGrantOption(privilege, role)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div className="col-span-1 py-2 px-2 flex justify-center">
                      <button
                        type="button"
                        onClick={() => handleDeleteGrant(privilege, role)}
                        className="text-gray-400 hover:text-red-600 focus:outline-none"
                        title="Delete grant"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              }),
            )}
          </>
        )}
      </div>
    </div>
  );
}
